import { languageKeys } from "@/language/translation";
import { DateTime } from "luxon";

type Translate = (key: string, params?: Record<string, unknown>) => unknown;

const LANGUAGE_KEYS = new Set<string>([...languageKeys]);
const TECHNICAL_HISTORY_KEYS = new Set([
  "id",
  "logicalId",
  "languageId",
  "nameId",
  "descriptionId",
]);

export function getLocalizedHistoryLanguageValue(value: unknown, locale: string) {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    return { isLanguageValue: false, label: undefined };
  }

  const labels = value as Record<string, unknown>;
  const keys = Object.keys(labels);
  if (keys.length === 0 || !keys.every((key) => LANGUAGE_KEYS.has(key))) {
    return { isLanguageValue: false, label: undefined };
  }

  const databaseLocale = locale.replaceAll("-", "_");
  const label = [
    labels[locale],
    labels[databaseLocale],
    labels.en,
    labels.en_GB,
    labels.de,
    labels.de_CH,
    ...Object.values(labels),
  ].find((candidate) => typeof candidate === "string" && candidate.trim().length > 0);

  return { isLanguageValue: true, label: label as string | undefined };
}

function comparableHistoryValue(value: unknown) {
  const languageValue = getLocalizedHistoryLanguageValue(value, "en");
  if (!languageValue.isLanguageValue) return value;
  if (languageValue.label == null) return null;

  const labels = value as Record<string, unknown>;
  return Object.fromEntries(
    [...LANGUAGE_KEYS].map((key) => {
      const label = labels[key];
      return [key, typeof label === "string" && label.trim().length > 0 ? label : null];
    }),
  );
}

export function isMeaningfulHistoryChange(oldValue: unknown, newValue: unknown) {
  return (
    JSON.stringify(comparableHistoryValue(oldValue)) !==
    JSON.stringify(comparableHistoryValue(newValue))
  );
}

export function formatEventActionHistoryDate(value: string) {
  const date = DateTime.fromISO(value);
  return date.isValid ? date.toFormat("dd.MM.yyyy, HH:mm") : value;
}

export function formatEventActionHistoryValue(
  value: unknown,
  locale: string,
  t: Translate,
  emptyValue = "–",
): string {
  if (value == null || value === "") return emptyValue;
  if (typeof value === "boolean") return String(t(value ? "global.yes" : "global.no"));

  const localized = getLocalizedHistoryLanguageValue(value, locale);
  if (localized.isLanguageValue) return localized.label ?? emptyValue;

  if (Array.isArray(value)) {
    const entries = value
      .map((entry) => formatEventActionHistoryValue(entry, locale, t, ""))
      .filter(Boolean);
    return entries.length > 0 ? entries.join(", ") : emptyValue;
  }
  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([key]) => !TECHNICAL_HISTORY_KEYS.has(key))
      .map(([key, entry]) => {
        const formatted = formatEventActionHistoryValue(entry, locale, t, "");
        return formatted === "" ? "" : `${formatEventActionHistoryProperty(key, t)}: ${formatted}`;
      })
      .filter(Boolean);
    return entries.length > 0 ? entries.join(" · ") : emptyValue;
  }
  return String(value);
}

export function formatEventActionHistoryProperty(property: string | undefined, t: Translate) {
  switch (property) {
    case "uniqueName":
      return String(t("license.customerManagement.Customer.internalName"));
    case "name":
    case "title":
      return String(t("global.name"));
    case "columns":
      return String(
        t("modules.sqdc.components.eventActionConfig.event.SQDCEventConfigComponent.columns"),
      );
    case "icon":
      return String(
        t("modules.sqdc.components.eventActionConfig.event.SQDCEventConfigComponent.icon"),
      );
    case "showLogoInModal":
    case "compactCreate":
    case "headerFooterInSidebar":
    case "idColor":
    case "description":
    case "rowSpan":
    case "columnSpan":
    case "isArray":
    case "range":
    case "relationHasCategory":
    case "userSelectionMode":
    case "dateGranularity":
    case "openEndTimeRange":
    case "showHours":
    case "required":
    case "showChangeInEABShortList":
    case "suffix":
    case "fontSize":
    case "bold":
    case "visibleForAdminOnly":
    case "lockAfterDays":
      return String(
        t(`modules.sqdc.components.eventActionConfig.event.SQDCEventConfigComponent.${property}`),
      );
    case "datapointTypeId":
      return String(
        t("modules.sqdc.components.eventActionConfig.event.SQDCEventConfigComponent.datapointType"),
      );
    case "prefix":
      return String(t("global.prefix"));
    case "type":
    case "internalType":
      return String(
        t("modules.sqdc.components.eventActionConfig.event.SQDCEventConfigComponent.type"),
      );
    case "boardAccess":
      return String(t("views.Board.boardAccess"));
    case "rowIndex":
      return String(
        t("modules.sqdc.components.eventActionConfig.SQDCEventActionTypeHistoryModal.row"),
      );
    case "columnIndex":
      return String(
        t("modules.sqdc.components.eventActionConfig.SQDCEventActionTypeHistoryModal.column"),
      );
    case "relation":
    case "enumValues":
      return String(
        t("modules.sqdc.components.eventActionConfig.event.SQDCEventConfigComponent.entity"),
      );
    case "preselectBoardRelation":
      return String(
        t(
          "modules.sqdc.components.eventActionConfig.event.SQDCEventConfigComponent.preselectCostCenter",
        ),
      );
    case "independentOfIshikawa":
    case "ishikawaConfig":
      return String(
        t("modules.sqdc.components.eventActionConfig.event.SQDCEventConfigComponent.ishikawa"),
      );
    case "teamSelection":
      return String(t("license.timeStampClock.UserShift.team"));
    case "isTitle":
      return String(
        t(
          "modules.sqdc.components.eventActionConfig.event.SQDCEventConfigComponent.eventTitleField",
        ),
      );
    case "showOnEAB":
      return String(
        t("modules.sqdc.components.eventActionConfig.event.SQDCEventConfigComponent.showOnEAB", {
          EABName: t("global.board"),
        }),
      );
    case "displayImage":
    case "imageFileId":
      return String(t("views.BoardTemplate.image"));
    case "added":
      return String(t("global.created"));
    case "removed":
      return String(t("global.deleted"));
    default:
      return property ?? "";
  }
}
